<template>
	<v-form ref="form" v-model="valid" style="height: 100%">
		<v-container fluid :class="loading ? 'fill-height' : ''">
			<v-row
				align="center"
				justify="center"
				v-if="loading"
			>
				<v-col cols="auto">
					<v-progress-circular indeterminate color="primary"></v-progress-circular>
				</v-col>
			</v-row>
			<template v-else>
				<!-- Заголовок -->
				<v-row>
					<v-col cols="auto" :class="['headline', 'pb-0', editMode ? 'pt-1' : 'pt-0']">
						<v-btn
							small
							color="primary"
							@click="back"
						>
							<v-icon>keyboard_arrow_left</v-icon>
						</v-btn>
					</v-col>
					<v-col class="headline" style="padding-top: 2px;">
						<template v-if="editMode">
							<v-text-field
								v-model="po.supplier"
								class="headline pt-0"
								placeholder="Наименование поставщика"
								single-line
								:rules="[
									rules.required,
									v => v.length <= 50 || 'Максимальная длина наименования поставщика - 50 символов'
								]"
							></v-text-field>
						</template>
						<template v-else>
							<div class="py-0">
								{{ po.supplier }}
							</div>
						</template>
					</v-col>
					<v-spacer></v-spacer>
					<v-col class="py-0" cols="auto">
						<v-menu
							v-model="topMenu"
							bottom left
							:close-on-content-click="false"
							v-if="viewMode"
						>
							<template v-slot:activator="{ on }">
								<v-btn icon v-on="on" class="d-print-none">
									<v-icon>more_vert</v-icon>
								</v-btn>
							</template>

							<v-list class="d-print-none">
								<v-list-item @click="splitOpen" :disabled="po.lines.length < 2">
									<v-list-item-icon>
										<v-icon style="transform: rotate(90deg)">call_split</v-icon>
									</v-list-item-icon>
									<v-list-item-title>
										Разделить
									</v-list-item-title>
								</v-list-item>
								<v-list-item @click="merge">
									<v-list-item-icon>
										<v-icon style="transform: rotate(90deg)">call_merge</v-icon>
									</v-list-item-icon>
									<v-list-item-title>
										Соединить
									</v-list-item-title>
								</v-list-item>
								<v-list-item @click="print">
									<v-list-item-icon>
										<v-icon>print</v-icon>
									</v-list-item-icon>
									<v-list-item-title>
										Печать
									</v-list-item-title>
								</v-list-item>
								<v-list-item>
									<v-switch
										v-model="printComments"
										dense
									>
										<template v-slot:label>
											<span class="pl-2">
												Печатать комментарии
											</span>
										</template>
									</v-switch>
								</v-list-item>
							</v-list>
						</v-menu>
					</v-col>
				</v-row>
				<!-- Шапка -->
				<v-row class="body-2">
					<v-col cols="12" sm="4">
						<v-row>
							<v-col class="py-2">
								PO №
							</v-col>
							<v-col class="py-2">
								<v-chip
									v-if="po.id > 0"
									small
									label
									color="primary"
								>
									<v-icon small left>credit_card</v-icon>
									{{ po.id }}
								</v-chip>
							</v-col>
						</v-row>
						<v-row>
							<v-col class="py-2">
								Автор
							</v-col>
							<v-col class="py-2">
								{{ po.authorName }}
							</v-col>
						</v-row>
						<v-row>
							<v-col class="py-2">
								Дата создания
							</v-col>
							<v-col class="py-2">
								{{ createdDateFormatted }}
							</v-col>
						</v-row>
						<v-row>
							<v-col class="py-2">
								Отель
							</v-col>
							<v-col :class="viewMode ? 'py-2' : 'py-1'">
								{{ po.hotelCode }}
							</v-col>
						</v-row>
					</v-col>
					<v-spacer v-if="$vuetify.breakpoint.smAndUp"></v-spacer>
					<v-col cols="12" sm="3" class="pb-0">
						<a-status-table
							:steps="po.steps"
							:status="po.status"
							:wrapped="$vuetify.breakpoint.xsOnly"
						></a-status-table>
					</v-col>
				</v-row>
				<!-- Таблица -->
				<v-row>
					<v-col class="pt-0">
						<v-data-table
							:headers="computedHeaders"
							:items="po.lines"
							disable-sort
							:items-per-page="-1"
							hide-default-header
							hide-default-footer
							:mobile-breakpoint="0"
						>
							<template v-slot:header="{ props: { headers } }">
								<thead>
									<tr>
										<th v-for="h in headers"
											:key="h.value"
											:class="'text-' + h.align"
										>
											<span
												v-if="h.value == 'sumGross'"
												style="cursor: pointer;"
												@click="showAllColumns = !showAllColumns"
											>
												{{ h.text }}
											</span>
											<span v-else>
												{{ h.text }}
											</span>
										</th>
									</tr>
								</thead>
							</template>
							<template v-slot:item.pr="{ item }">
								<v-chip
									small
									label
									color="primary"
									:to="{ name: 'PrEntry', params: { id : item.requestId } }"
								>
									<v-icon small left>shopping_basket</v-icon>
									{{ item.requestId }}
								</v-chip>
							</template>
							<template v-slot:item.priceNet="{ item }">
								<span class="text-no-wrap">
									{{ item.priceNet }}
								</span>
							</template>
							<template v-slot:item.sumNet="{ item }">
								<span class="text-no-wrap">
									{{ item.sumNet }}
								</span>
							</template>
							<template v-slot:item.sumGross="{ item }">
								<span class="text-no-wrap">
									{{ item.sumGross }}
								</span>
							</template>
							<template v-slot:item.toPay="{ item }">
								<span class="text-no-wrap">
									{{ item.toPay }}
								</span>
							</template>
							<template v-slot:body.append="{ headers }">
								<tr class="subtitle-2 font-weight-bold">
									<td :colspan="headers.length - 1" class="text-right">
										Итого
									</td>
									<td class="text-center text-no-wrap">
										{{ toStr(totalToPay) }}
									</td>
								</tr>
							</template>
						</v-data-table>
					</v-col>
				</v-row>
				<!-- Файлы -->
				<v-row>
					<v-col cols="12">
						<a-file-chips
							:files.sync="files"
							:uploaded-files.sync="po.files"
							:edit-mode="editMode"
						></a-file-chips>
					</v-col>
				</v-row>
				<!-- Кнопки -->
				<v-row
					v-if="viewMode"
					class="d-print-none"
				>
					<v-col cols="auto">
						<div v-intersect="intersectButtons">
							<template
								v-for="button in po.buttons"
							>
								<v-btn
									v-if="button.visible"
									:key="button.value"
									color="primary"
									class="ma-2"
									:loading="button.loading"
									:disabled="button.disabled"
									@click="actionController(button.value)"
								>
									<v-icon color="green" left v-if="button.value == 'approve'">check_circle</v-icon>
									<v-icon color="red" left v-if="button.value == 'reject'">remove_circle</v-icon>
									<v-icon color="white" left v-if="button.value == 'withdraw'">undo</v-icon>
									<v-icon color="red" left v-if="button.value == 'delete'">delete</v-icon>
									<v-icon color="deep-orange" left v-if="button.value == 'edit'">edit</v-icon>
									{{ button.text }}
								</v-btn>
							</template>
						</div>
					</v-col>
					<v-spacer></v-spacer>
					<v-col cols="auto">
						<v-switch
							class="ma-2"
							label="Показать историю редактирования"
							v-model="showChanges"
						></v-switch>
					</v-col>
				</v-row>

				<!-- Кнопка в режиме редактирования -->
				<v-row
					v-else-if="editMode"
					class="d-print-none"
				>
					<v-col cols="12">
						<v-btn
							color="primary"
							class="ma-2"
							@click="submitEditing"
							:disabled="!valid"
							v-intersect="intersectButtons"
						>
							Готово
						</v-btn>
						<v-btn
							color="secondary"
							class="ma-2"
							@click="cancelEditing"
							v-intersect="intersectButtons"
						>
							Отмена
						</v-btn>
					</v-col>
				</v-row>
				<!-- Комментарии -->
				<v-row v-intersect="intersectComments" :class="printComments ? '' : 'd-print-none'">
					<v-col cols="12">
						<a-comments-block
							v-if="po.id > 0"
							ref="comments"
							type="po"
							:id="po.id"
							:changes="showChanges"
							:allow-files="false"
						></a-comments-block>
					</v-col>
				</v-row>

				<v-snackbar
					v-model="snack.info.show"
					:timeout="3000"
					bottom right
				>
					<v-icon
						v-if="snack.info.icon.name.length"
						:color="snack.info.icon.color"
					>
						{{ snack.info.icon.name }}
					</v-icon>
					{{ snack.info.message }}
				</v-snackbar>

				<!-- Диалог split -->
				<v-dialog
					v-model="split.show"
					max-width="1200px"
				>
					<v-card>
						<v-card-title>
							<span class="headline">
								Разделение ордера
							</span>
						</v-card-title>
						<v-card-text>
							<span class="caption pl-3">
								Выберите строки, которые будут выделены в новый ордер
							</span>

							<v-data-table
								v-model="split.selected"
								:headers="computedHeaders"
								:items="po.lines"
								item-key="num"
								show-select
								disable-sort
								:items-per-page="-1"
								hide-default-footer
								@click:row="splitClickRow"
							>
								<template v-slot:header.data-table-select="{ on, props }">
									<!-- <v-checkbox
										hide-details
										class="mt-0"
										color="secondary"
										v-bind="props"
										v-on="on"
									></v-checkbox> -->
								</template>
								<template v-slot:item.data-table-select="{ isSelected, select }">
									<v-checkbox
										hide-details
										class="mt-0"
										color="secondary"
										:value="isSelected"
										@input="select($event)"
									></v-checkbox>
								</template>
								<template v-slot:item.pr="{ item }">
									<v-chip
										small
										label
										color="primary"
										:to="{ name: 'PrEntry', params: { id : item.requestId } }"
									>
										<v-icon small left>shopping_basket</v-icon>
										{{ item.requestId }}
									</v-chip>
								</template>
							</v-data-table>
						</v-card-text>
						<v-card-actions>
							<template>
								<v-btn
									:disabled="split.selected.length == 0 || split.selected.length == po.lines.length"
									:loading="split.loading"
									text
									@click="makeSplit"
								>
									Разделить
								</v-btn>
								<v-btn
									color="secondary"
									text
									@click="splitClose"
								>
									Отмена
								</v-btn>
							</template>
						</v-card-actions>
					</v-card>
				</v-dialog>

			</template>
		</v-container>
		<!-- Висящие кнопки -->
		<div
			v-if="viewMode && !intersectingButtons && !intersectingComments"
			class="pa-3 d-print-none"
			style="position: fixed; bottom: 0px;"
		>
			<template
				v-for="button in po.buttons"
			>
				<v-btn
					v-if="button.visible && button.fixed"
					:key="button.value"
					color="primary"
					class="ma-2"
					:loading="button.loading"
					@click="actionController(button.value)"
				>
					<v-icon color="green" left v-if="button.value == 'approve'">check_circle</v-icon>
					<v-icon color="red" left v-if="button.value == 'reject'">remove_circle</v-icon>
					<v-icon color="white" left v-if="button.value == 'withdraw'">undo</v-icon>
					<v-icon color="red" left v-if="button.value == 'delete'">delete</v-icon>
					<v-icon color="deep-orange" left v-if="button.value == 'edit'">edit</v-icon>
					{{ button.text }}
				</v-btn>
			</template>
		</div>
		<div
			v-if="editMode && !intersectingButtons && !intersectingComments"
			class="pa-3 d-print-none"
			style="position: fixed; bottom: 0px;"
		>
			<v-btn
				color="primary"
				class="ma-2"
				:disabled="!valid"
				@click="submitEditing"
			>
				Готово
			</v-btn>
			<v-btn
				color="secondary"
				class="ma-2"
				@click="cancelEditing"
			>
				Отмена
			</v-btn>
		</div>
	</v-form>
</template>

<script>
	import _ from 'lodash'
	import { api } from '../../lib/api'
	import { toNum, toStr, getMonthName, dateFormat, linkify } from '../../js/common'
	import { systemComment, systemIcon } from '../../js/po.js'
	import aStatusTable from '../base/AStatusTable.vue'
	import aCommentsBlock from '../base/ACommentsBlock.vue'
	import aFileChips from '../base/AFileChips.vue'

	var mixinSnack = {
		data: function () {
			return {
				snack: {
					info: {
						show: false,
						message: '',
						icon: {
							name: '',
							color: ''
						},
					},
				},
			}
		},
		methods: {
			showSnackInfo(action) {
				this.snack.info.show = false

				this.snack.info.message = systemComment(action)
				this.snack.info.icon = systemIcon(action)
				this.snack.info.show = true
			},
		}
	}
	var mixinSplit = {
		data: function () {
			return {
				split: {
					show: false,
					selected: [],
					loading: false
				},
			}
		},
		methods: {
			makeSplit() {
				this.split.loading = true
				api.post('po/split', {
					id: this.po.id,
					rows: this.split.selected.map(row => row.id)
				})
				.then(response => {
					this.$store.dispatch('dialogNotify', {
						title: 'Ордер разделен',
						message: 'Созданы новые ордера '
							+ `${response.data.ids[0]}`
							+ ' и '
							+ `${response.data.ids[1]}`
					})
					this.splitClose()
					this.$router.push({ name: 'PoList' })
				}, error => {})
			},
			splitClose() {
				this.split.show = false
				this.split.selected = []
				this.split.loading = false
			},
			splitOpen() {
				this.topMenu = false
				this.split.show = true
			},
			splitClickRow(row) {
				let i = this.split.selected.indexOf(row)
				if (i > -1) {
					this.split.selected.splice(i, 1)
				}
				else {
					this.split.selected.push(row)
				}
			}
		},
	}
	var mixinSearch = {
		data: function () {
			return {
				filters: [],
				search: '',
				showAll: false
			}
		}
	}

	export default {
		mixins: [
			mixinSnack,
			mixinSplit,
			mixinSearch
		],
		components: {
			aStatusTable,
			aCommentsBlock,
			aFileChips,
		},
		data: () => ({
			editMode: false,
			files: [],
			headers: [
				{
					value: 'num',
					text: '№'
				},
				{
					value: 'pr',
					text: 'Заявка на расходы',
					align: 'center'
				},
				{
					value: 'name',
					text: 'Наименование товаров / услуг',
					align: 'center'
				},
				{
					value: 'unit',
					text: 'Ед. изм.',
					align: 'center'
				},
				{
					value: 'qty',
					text: 'Кол-во',
					align: 'center'
				},
				{
					value: 'priceNet',
					text: 'Цена без НДС',
					align: 'center'
				},
				{
					value: 'sumNet',
					text: 'Сумма без НДС',
					align: 'center'
				},
				{
					value: 'sumGross',
					text: 'Сумма с НДС',
					align: 'center'
				},
				{
					value: 'percent',
					text: 'Процент оплаты',
					align: 'center'
				},
				{
					value: 'toPay',
					text: 'К оплате',
					align: 'center'
				},
			],
			intersectingButtons: false,
			intersectingComments: false,
			loading: false,
			po: {
				id: 0,
				supplier: '',
				hotelCode: '',
				createdDate: '',
				author: '',
				status: 0,
				steps: [],
				lines: [],
				files: [],
				buttons: [],
			},
			printComments: false,
			rules: {
				required: v => !!v || 'Обязательное поле',
			},
			showAllColumns: false,
			showChanges: false,
			topMenu: false,
			updateInterval: 300000,	// 5 минут
			valid: false,
		}),
		computed: {
			computedHeaders() {
				if (this.showAllColumns) return this.headers
				else return this.headers.filter(h =>
					h.value != 'unit'
					&& h.value != 'qty'
					&& h.value != 'priceNet'
					&& h.value != 'sumNet'
				)
			},
			createdDateFormatted() {
				return dateFormat(this.po.createdDate)
			},
			totalSumGross() {
				return this.po.lines.reduce((acc, cur) => {
					return acc + toNum(cur.sumGross)
				}, 0)
			},
			totalSumNet() {
				return this.po.lines.reduce((acc, cur) => {
					return acc + toNum(cur.sumNet)
				}, 0)
			},
			totalToPay() {
				return this.po.lines.reduce((acc, cur) => {
					return acc + toNum(cur.toPay)
				}, 0)
			},
			viewMode() {
				return !this.editMode
			},
		},
		methods: {
			actionController(value) {
				if (value == 'edit') this.edit()
				else if (value == 'approve') this.approve()
				else if (value == 'reject') this.reject()
				else if (value == 'drop') this.drop()
				else if (value == 'delete') this.deletePo()
				else if (value == 'withdraw') this.withdraw()
			},
			approve() {
				this.startButtonLoading('approve')

				let params = {
					id: this.po.id
				}

				api.post('po/approve', params)
				.then(response => {

					this.stopButtonLoading('approve')
					this.po.buttons = response.data.buttons
					this.po.status = response.data.status

					this.loadComments()

					this.showSnackInfo(response.data.sysComment)

					this.$store.dispatch('loadMenuCounters')
				}, error => {
					this.stopButtonLoading('approve')
				})
			},
			back() {
				if (this.search.length || this.filters.length || this.showAll) {
					this.$router.push({
						name: 'PoList',
						params: {
							search: this.search,
							filters: this.filters,
							showAll: this.showAll
						}
					})
				}
				else {
					this.$router.push({
						name: 'PoList'
					})
				}
			},
			cancelEditing() {
				this.files.splice(0, this.files.length)

				this.$refs.form.resetValidation()

				this.editMode = false

				this.load()
			},
			deletePo() {
				this.$store.dispatch('dialogConfirm', {
					title: 'Вы уверены?',
					message: 'Удалить ордер?'
				})
				.then(confirm => {
					if (confirm) {
						this.startButtonLoading('delete')
						api.post('po/delete', {
							id: this.po.id
						})
						.then(response => {
							this.$router.push('/po')
							this.stopButtonLoading('delete')

						}, error => {
							this.stopButtonLoading('delete')
						})
					}
				})
			},
			drop() {
				this.startButtonLoading('drop')

				let params = {
					id: this.po.id
				}

				api.post('po/drop', params)
				.then(response => {

					this.stopButtonLoading('drop')
					this.po.buttons = response.data.buttons
					this.po.status = response.data.status

					this.loadComments()

					this.showSnackInfo(response.data.sysComment)

					this.$store.dispatch('loadMenuCounters')
				}, error => {
					this.stopButtonLoading('drop')
				})
			},
			edit() {
				this.editMode = true
			},
			intersectButtons(entries, observer) {
				this.intersectingButtons = entries[0].isIntersecting
			},
			intersectComments(entries, observer) {
				this.intersectingComments = entries[0].isIntersecting
			},
			load(loading) {
				let time = new Date
				// console.log(time.toTimeString().substr(0, 8), 'load')

				if (loading === undefined) loading = false

				this.loading = loading

				return api.post('po/get_entry', {
					id: this.po.id,
				})
				.then(response => {

					if (this.viewMode) {
						this.po = response.data.po
					}

					this.loading = false

				}, error => {})
			},
			loadComments() {
				if (this.$refs.comments) this.$refs.comments.load()
			},
			merge() {
				this.$router.push({
					name: 'PoList',
					params: {
						initialSelected: this.po.id
					}
				})
			},
			print() {

				this.$emit('print')
				this.topMenu = false

			},
			reject() {
				this.startButtonLoading('reject')

				let params = {
					id: this.po.id
				}

				api.post('po/reject', params)
				.then(response => {

					this.stopButtonLoading('reject')
					this.po.buttons = response.data.buttons
					this.po.status = response.data.status

					this.loadComments()

					this.showSnackInfo(response.data.sysComment)

					this.$store.dispatch('loadMenuCounters')
				}, error => {
					this.stopButtonLoading('reject')
				})
			},
			startButtonLoading(value) {
				this.po.buttons.find(b => b.value == value).loading = true
			},
			stopButtonLoading(value) {
				this.po.buttons.find(b => b.value == value).loading = false
			},
			submitEditing() {
				if (!this.$refs.form.validate()) {
					this.$vuetify.goTo(0)
					return
				}

				// console.log('submitEditing')
				this.loading = true

				var formData = new FormData()

				let l = this.files.length
				for (let j = 0; j < l; j++) {
					// console.log(i, j, this.files[i][j])
					formData.append(`files[]`, this.files[j])
				}

				formData.append('form', JSON.stringify({
					po: _.pick(this.po, [
						'id',
						'supplier',
						'files'
					])
				}))

				api.post('po/change', formData)
				.then(response => {
					this.editMode = false

					this.files.splice(0, this.files.length)

					this.po.buttons = response.data.buttons
					this.po.status = response.data.status

					if (response.data.files) {
						this.po.files = response.data.files
					}

					this.loading = false

				}, error => {
					this.loading = false
				})
			},
			toStr(val) {
				return toStr(val)
			},
			withdraw() {
				this.startButtonLoading('withdraw')
				api.post('po/withdraw', {
					id: this.po.id,
				})
				.then(response => {

					this.stopButtonLoading('withdraw')
					this.po.buttons = response.data.buttons
					this.po.status = response.data.status

					this.loadComments()

					this.showSnackInfo(response.data.sysComment)

					this.$store.dispatch('loadMenuCounters')
				}, error => {
					this.stopButtonLoading('withdraw')
				})
			},
		},
		created() {
			this.throttledLoad = _.throttle(this.load, 10000)
			this.throttledLoadComments = _.throttle(this.loadComments, 10000)
		},
		mounted() {
			this.po.id = +this.$route.params.id

			if (this.$route.params.search !== undefined) this.search = this.$route.params.search
			if (this.$route.params.filters !== undefined) this.filters = this.$route.params.filters
			if (this.$route.params.showAll !== undefined) this.showAll = this.$route.params.showAll

			this.load(true)

			this.timer = setInterval(() => {
				if (!document.hidden) {
					if (this.viewMode) {
						this.throttledLoad()
					}
					this.throttledLoadComments()
				}
			}, this.updateInterval)

			window.onfocus = () => {
				// console.log('onfocus')
				if (this.viewMode) {
					this.throttledLoad()
				}
				this.throttledLoadComments()
			}
		},
		destroyed() {
			clearInterval(this.timer)
			window.onfocus = null
		},
		beforeRouteUpdate (to, from, next) {
			this.po.id = to.params.id

			this.showAllColumns = false
			this.load(true)

			next()
		},
}
</script>

<style>
</style>
